import { Link } from 'react-router-dom';
import { Cookie, Shield, ChartLineUp, Megaphone, ArrowLeft } from '@phosphor-icons/react';
import { openCookiePreferences, COOKIE_CONSENT_VERSION } from '../components/CookieBanner.jsx';

export default function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-zinc-50 py-12 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto bg-white border border-zinc-200 p-6 sm:p-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-900 mb-6">
          <ArrowLeft size={12} weight="bold" /> Indietro
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 flex items-center justify-center">
            <Cookie size={22} weight="fill" className="text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-zinc-900 tracking-tight">Cookie Policy</h1>
            <p className="text-[11px] text-zinc-500 mt-0.5">Ultimo aggiornamento: febbraio 2026 · v{COOKIE_CONSENT_VERSION}</p>
          </div>
        </div>

        <div className="prose prose-zinc max-w-none text-sm leading-relaxed text-zinc-700">
          <p>
            Questa pagina descrive in modo trasparente quali cookie utilizziamo su Locazione Turistica, perché li usiamo
            e come puoi gestire le tue preferenze. Lo facciamo in conformità al Regolamento UE 2016/679 (GDPR), alla
            Direttiva ePrivacy 2002/58/CE e alle Linee Guida del Garante Privacy del 10 giugno 2021.
          </p>

          <h2 className="text-base font-black text-zinc-900 mt-8 mb-3">Cosa sono i cookie</h2>
          <p>
            I cookie sono piccoli file di testo che i siti memorizzano sul tuo dispositivo per riconoscerti,
            mantenerti loggato, ricordare preferenze o raccogliere statistiche d'uso. Alcuni sono indispensabili
            (senza di essi l'app non funziona), altri sono facoltativi.
          </p>

          <h2 className="text-base font-black text-zinc-900 mt-8 mb-4">Categorie di cookie utilizzate</h2>

          <Category
            icon={Shield}
            title="Strettamente necessari"
            badge="Sempre attivi · base giuridica art. 6.1.b GDPR"
            description="Indispensabili per autenticazione, gestione della sessione, sicurezza CSRF, processo di pagamento Stripe e accessibilità. Senza questi cookie non potresti accedere o operare in modo sicuro."
            details={[
              { name: 'access_token', purpose: 'Sessione di autenticazione JWT', duration: '60 minuti', provider: 'Locazione Turistica (1ª parte)' },
              { name: 'refresh_token', purpose: 'Rinnovo automatico sessione', duration: '7 giorni', provider: 'Locazione Turistica (1ª parte)' },
              { name: '__stripe_mid / __stripe_sid', purpose: 'Frode prevenzione e checkout Stripe', duration: '1 anno / 30 minuti', provider: 'Stripe (3ª parte)' },
              { name: 'cookie_consent_v2', purpose: 'Memorizza la tua scelta sui cookie', duration: '26 mesi', provider: 'Locazione Turistica (1ª parte)' },
            ]}
          />

          <Category
            icon={ChartLineUp}
            title="Statistiche anonime"
            badge="Opzionali · base giuridica art. 6.1.a GDPR (consenso)"
            description="Usati solo se acconsenti. Ci aiutano a capire quali funzionalità sono più utili e dove migliorare. I dati sono pseudo-anonimizzati: non vendiamo informazioni a terzi e non costruiamo profili pubblicitari."
            details={[
              { name: 'ph_*', purpose: 'Analytics di utilizzo (PostHog)', duration: '12 mesi', provider: 'PostHog Inc., USA (3ª parte) · DPA firmato' },
            ]}
          />

          <Category
            icon={Megaphone}
            title="Marketing & promo"
            badge="Opzionali · base giuridica art. 6.1.a GDPR (consenso)"
            description="Al momento NON utilizziamo cookie pubblicitari di terze parti. Se in futuro attiveremo retargeting Meta Pixel, Google Ads o email marketing avanzato, lo faremo solo se hai dato consenso esplicito in questa categoria."
            details={[
              { name: '—', purpose: 'Nessuno al momento · placeholder', duration: '—', provider: '—' },
            ]}
          />

          <h2 className="text-base font-black text-zinc-900 mt-10 mb-3">Come gestire le tue preferenze</h2>
          <p>Hai sempre il pieno controllo:</p>
          <ul className="space-y-2 mt-3">
            <li>
              <button
                type="button"
                onClick={openCookiePreferences}
                className="text-emerald-700 underline hover:text-emerald-900 font-medium"
                data-testid="cookie-page-open-prefs"
              >
                Apri il pannello preferenze cookie →
              </button>
            </li>
            <li>Dal browser puoi bloccare o eliminare i cookie esistenti (occhio: i necessari sono indispensabili).</li>
            <li>Puoi revocare il consenso in qualsiasi momento — anche dal footer di ogni pagina ("Gestisci preferenze cookie").</li>
          </ul>

          <h2 className="text-base font-black text-zinc-900 mt-10 mb-3">Trasferimenti extra-UE</h2>
          <p>
            <strong>PostHog</strong> ha server negli Stati Uniti. Il trasferimento è coperto dalle Clausole Contrattuali
            Standard (SCC) approvate dalla Commissione Europea. <strong>Stripe</strong> opera con la sua entità irlandese
            (UE) per i clienti europei.
          </p>

          <h2 className="text-base font-black text-zinc-900 mt-10 mb-3">I tuoi diritti</h2>
          <p>
            Sotto il GDPR puoi: chiedere accesso ai tuoi dati (Art. 15), rettificarli (Art. 16), cancellarli (Art. 17,
            "diritto all'oblio"), limitarne il trattamento (Art. 18), opporti (Art. 21) e revocare il consenso in qualsiasi
            momento. Per esercitare questi diritti scrivici a{' '}
            <a href="mailto:vitadahost@gmail.com" className="text-emerald-700 underline hover:text-emerald-900">vitadahost@gmail.com</a>{' '}
            — risponderemo entro 30 giorni.
          </p>

          <h2 className="text-base font-black text-zinc-900 mt-10 mb-3">Contatti</h2>
          <p>
            <strong>Titolare del trattamento:</strong> Locazione Turistica · Vita da Host<br />
            <strong>Email:</strong> <a href="mailto:vitadahost@gmail.com" className="text-emerald-700 underline">vitadahost@gmail.com</a><br />
            <strong>Sede:</strong> Genova, Italia
          </p>

          <p className="text-xs text-zinc-500 mt-12 border-t border-zinc-100 pt-4">
            Documento mantenuto in linea con le evoluzioni normative.
            Per la Privacy Policy completa visita la pagina{' '}
            <Link to="/privacy" className="underline text-zinc-700 hover:text-zinc-900">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}

function Category({ icon: Icon, title, badge, description, details }) {
  return (
    <div className="border border-zinc-200 p-5 mb-4 not-prose">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 bg-zinc-50 border border-zinc-200 flex items-center justify-center flex-shrink-0">
          <Icon size={16} weight="bold" className="text-zinc-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-black uppercase tracking-[0.18em] text-zinc-500 mb-1">{badge}</p>
          <h3 className="text-sm font-black text-zinc-900 tracking-tight">{title}</h3>
        </div>
      </div>
      <p className="text-[13px] text-zinc-600 leading-relaxed mb-4">{description}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-zinc-200 text-left">
              <th className="font-black uppercase tracking-wider text-zinc-500 py-1.5 pr-2">Cookie</th>
              <th className="font-black uppercase tracking-wider text-zinc-500 py-1.5 pr-2">Scopo</th>
              <th className="font-black uppercase tracking-wider text-zinc-500 py-1.5 pr-2">Durata</th>
              <th className="font-black uppercase tracking-wider text-zinc-500 py-1.5">Provider</th>
            </tr>
          </thead>
          <tbody>
            {details.map((d, i) => (
              <tr key={i} className="border-b border-zinc-100">
                <td className="py-1.5 pr-2 font-mono text-[10.5px] text-zinc-900">{d.name}</td>
                <td className="py-1.5 pr-2 text-zinc-700">{d.purpose}</td>
                <td className="py-1.5 pr-2 text-zinc-600 tabular-nums">{d.duration}</td>
                <td className="py-1.5 text-zinc-600">{d.provider}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
